import { useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { PaymentMethodIcon } from './CardBrandIcon';

export type FallbackOption = {
  id: string;       // Stripe PaymentMethod ID. '' is reserved for the Zelle (manual) option.
  label: string;
  kind: 'card' | 'bank_account';
  sourceId?: string; // setup-intent ID for banks; PM ID for cards
  brand?: string | null; // card brand for the brand icon
};

/**
 * Prominent card-style dropdown for choosing the BHCS Cash fallback payment method.
 * Presentational only — the caller owns the selected value, the option list, and what
 * happens on change (persist, refresh a breakdown, etc.). The empty-string value ('')
 * represents the Zelle / pay-the-remainder-manually option.
 *
 * Shared by the BHCS Cash page (Wallet) and the checkout split-pay notice so both use
 * the exact same switcher.
 */
export function FallbackMethodPicker({
  value,
  onChange,
  options,
  testId,
  ariaLabel = 'Fallback payment method',
}: {
  value: string;
  onChange: (id: string) => void;
  options: FallbackOption[];
  testId?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  // Open upward when there isn't room below (e.g. near the mobile bottom nav), so the
  // last option is never cut off.
  const [dropUp, setDropUp] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selected = value ? options.find((o) => o.id === value) : null;

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const NAV_SAFE = 88; // leave room for the fixed bottom nav
        const spaceBelow = window.innerHeight - rect.bottom - NAV_SAFE;
        const spaceAbove = rect.top - 16;
        const panelHeight = Math.min(288, (options.length + 1) * 68 + 8);
        setDropUp(spaceBelow < panelHeight && spaceAbove > spaceBelow);
      }
      return next;
    });
  };

  const list = [
    { id: '', kind: 'zelle' as const, label: 'Zelle', sub: 'Pay the remainder manually', brand: null as string | null },
    ...options.map((o) => ({
      id: o.id,
      kind: o.kind,
      label: o.label,
      sub: 'Auto-charged for any remaining balance',
      brand: o.brand ?? null,
    })),
  ];

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        data-testid={testId}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 bg-white text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          open ? 'border-blue-500' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40'
        }`}
      >
        <span className="flex items-center min-w-0 gap-3">
          <PaymentMethodIcon type={selected ? selected.kind : 'zelle'} brand={selected?.brand ?? undefined} size={26} />
          <span className="min-w-0">
            <span className="block text-sm sm:text-base font-medium text-gray-900 truncate">
              {selected ? selected.label : 'Zelle'}
            </span>
            <span className="block text-xs text-gray-500">
              {selected ? 'Auto-charged for any remaining balance' : 'Pay the remainder manually'}
            </span>
          </span>
        </span>
        <ChevronDown className={`w-5 h-5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="listbox"
            className={`absolute z-20 w-full max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg py-1 ${
              dropUp ? 'bottom-full mb-2' : 'top-full mt-2'
            }`}
          >
            {list.map((opt) => {
              const isSelected = value === opt.id;
              return (
                <button
                  key={opt.id || 'zelle'}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(opt.id);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <PaymentMethodIcon type={opt.kind} brand={opt.brand ?? undefined} size={24} />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm truncate ${isSelected ? 'font-semibold text-blue-900' : 'font-medium text-gray-900'}`}>
                      {opt.label}
                    </span>
                    <span className="block text-xs text-gray-500">{opt.sub}</span>
                  </span>
                  {isSelected && <Check className="w-4 h-4 shrink-0 text-blue-600" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
